import EmptyState from "./EmptyState";

const VARIANTS = {
  default: {
    loadingWrap: "rounded-2xl border border-cream/60 bg-white",
    loadingIcon: "text-primary-dark",
    outer: "rounded-2xl border border-cream/60 bg-white",
    table: "min-w-full divide-y divide-cream/60 text-left text-sm text-slate-700",
    thead: "bg-cream/60 uppercase tracking-[0.2em] font-bold text-primary-dark",
    rowBorder: "border-b border-cream/40 last:border-0",
    thCell: "px-4 py-3 text-xs",
    tdCell: "px-4 py-3",
    mobileWrap: "space-y-3 p-3 md:hidden",
    mobileCard:
      "rounded-xl border border-cream/60 bg-white p-4 shadow-sm",
    mobileLabel: "text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500",
    mobileValue: "text-sm text-slate-700",
    mobileActionsBorder: "border-t border-cream/60 pt-3",
    fetchingBar: "border-t border-cream/60 bg-cream/40 px-4 py-2 text-xs text-slate-500",
  },
  theme: {
    loadingWrap: "rounded-2xl border border-border bg-surface",
    loadingIcon: "text-primary",
    outer: "rounded-2xl border border-border bg-surface shadow-sm",
    table:
      "min-w-full divide-y divide-border text-left text-sm text-secondary",
    thead:
      "bg-surface-muted normal-case tracking-normal text-xs font-semibold text-text-muted",
    rowBorder: "border-b border-border last:border-0",
    thCell: "px-4 py-3 text-xs",
    tdCell: "px-4 py-3",
    mobileWrap: "space-y-3 p-3 md:hidden",
    mobileCard:
      "rounded-xl border border-border bg-surface p-4 shadow-sm",
    mobileLabel:
      "text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted",
    mobileValue: "text-sm text-secondary",
    mobileActionsBorder: "border-t border-border pt-3",
    fetchingBar:
      "border-t border-border bg-surface-muted px-4 py-2 text-xs text-text-muted",
  },
  /** Quiet chrome: no header band, lighter dividers, compact cells */
  themeMinimal: {
    loadingWrap: "rounded-xl border border-border bg-surface",
    loadingIcon: "text-primary",
    outer: "overflow-hidden rounded-xl border border-border bg-surface",
    table:
      "min-w-full border-collapse text-left text-sm text-secondary",
    thead: "border-b border-border bg-transparent",
    rowBorder:
      "transition-colors border-b border-border/50 last:border-0 hover:bg-surface-muted/35",
    thCell:
      "px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-text-muted",
    tdCell: "px-3 py-2.5 align-middle",
    mobileWrap: "space-y-2 p-2 md:hidden",
    mobileCard: "rounded-lg border border-border/80 bg-surface px-3 py-2.5",
    mobileLabel:
      "text-[10px] font-medium uppercase tracking-wider text-text-muted",
    mobileValue: "text-sm text-secondary",
    mobileActionsBorder: "mt-2 border-t border-border/60 pt-2.5",
    fetchingBar:
      "border-t border-border/80 bg-surface-muted/50 px-3 py-2 text-xs text-text-muted",
  },
};

const DataTable = ({
  columns,
  rows,
  getRowKey = (row, index) => row._id || index,
  getRowClassName,
  rowClassName = "",
  isLoading = false,
  isFetching = false,
  isError = false,
  error,
  emptyTitle = "No records",
  emptyMessage = "There are no records to display.",
  emptyIcon = null,
  responsiveCards = false,
  /** Use design tokens (border, surface, text-*) instead of cream/slate */
  variant = "default",
}) => {
  const v = VARIANTS[variant] ?? VARIANTS.default;
  const thCell = v.thCell ?? "px-4 py-3 text-xs";
  const tdCell = v.tdCell ?? "px-4 py-3";
  // ================= LOADING =================
  if (isLoading) {
    return (
      <div className={`relative ${v.loadingWrap}`}>
        <div className="flex min-h-[200px] items-center justify-center py-12">
          <div className={`flex items-center gap-2 ${v.loadingIcon}`}>
            <svg
              className="h-6 w-6 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
            <span className={`font-medium text-sm`}>Loading…</span>
          </div>
        </div>
      </div>
    );
  }

  // ================= ERROR =================
  if (isError) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Unable to load data.";

    return (
      <EmptyState
        title="Action required"
        description={message}
        icon={emptyIcon}
        descriptionClassName="text-red-600"
      />
    );
  }

  // ================= EMPTY =================
  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyMessage}
        icon={emptyIcon}
      />
    );
  }

  // ================= HELPERS =================
  const renderCellContent = (col, row, index) =>
    typeof col.render === "function"
      ? col.render(row, index)
      : row[col.key];

  // ================= MOBILE CARDS =================
  const renderMobileCards = () => (
    <div className={v.mobileWrap}>
      {rows.map((row, index) => {
        const regularColumns = columns.filter(
          (col) => !(col.key === "actions" || col.align === "right")
        );

        const actionColumns = columns.filter(
          (col) => col.key === "actions" || col.align === "right"
        );

        return (
          <div
            key={getRowKey(row, index)}
            className={`${v.mobileCard} ${rowClassName} ${
              typeof getRowClassName === "function"
                ? getRowClassName(row, index)
                : ""
            }`}
          >
            <div className="space-y-3">
              {regularColumns.map((col) => {
                const label =
                  typeof col.mobileHeader === "string" && col.mobileHeader !== ""
                    ? col.mobileHeader
                    : typeof col.header === "string"
                    ? col.header
                    : "";

                return (
                  <div key={col.key || label} className="min-w-0 space-y-1">
                    {label ? (
                      <p className={v.mobileLabel}>
                        {label}
                      </p>
                    ) : null}
                    <div className={v.mobileValue}>
                      {renderCellContent(col, row, index)}
                    </div>
                  </div>
                );
              })}

              {actionColumns.length > 0 && (
                <div className={v.mobileActionsBorder}>
                  <p className={`mb-2 ${v.mobileLabel}`}>
                    Actions
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {actionColumns.map((col) => (
                      <div key={col.key || "actions"}>
                        {renderCellContent(col, row, index)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const tableWrapClass = responsiveCards
    ? "hidden md:block overflow-x-auto"
    : "overflow-x-auto";

  // ================= TABLE =================
  return (
    <div className={v.outer}>
      {responsiveCards ? renderMobileCards() : null}
      <div className={`${responsiveCards ? 'hidden md:block' : ''} overflow-x-auto`}>
        <table className={v.table}>
          <thead className={v.thead}>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key || col.header}
                  className={`${thCell} ${col.headerClassName || ''} ${col.align === 'right' ? 'text-right' : ''}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, index) => (
              <tr
                key={getRowKey(row, index)}
                className={`${v.rowBorder} ${rowClassName} ${
                  typeof getRowClassName === "function"
                    ? getRowClassName(row, index)
                    : ""
                }`}
              >
                {columns.map((col) => (
                  <td
                    key={col.key || col.header}
                    className={`${tdCell} ${col.className || ''} ${col.align === 'right' ? 'text-right' : ''}`}
                  >
                    {renderCellContent(col, row, index)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isFetching && (
        <div className={v.fetchingBar}>
          Refreshing…
        </div>
      )}
    </div>
  );
};

export default DataTable;
